<template>
  <div>
    <popup-hint hint="spirits">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Spirits">
          <template v-slot:left v-if="lowInventoryCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{lowInventoryCount}} spirit{{lowInventoryCount>1?'s':''}} with low inventory
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.siteId=null;filters.status='1';filters.search='';filters.lowInventory='1';filters.license='0';filters.asof=today})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Spirits are essential for most production.
      </template>
      <template v-slot:bottom>
        You can create spirits individually or do a batch import of spirits.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'SpiritsEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <import-csv v-model="doImport" type="spirits" :fields="importFields" importAction="spiritsImport" :instructions="instructions" ref="import" @refresh="$refs.table.buildList()"/>

    <purtrak-table name="spirits" objectType="spirits" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select
                   listEndPoint="spirits2" v-model="selected"
                   fields="id,name,active,sku,preferredVendor{id},reorderPoint,onOrder,onSalesOrder,lowInventory,
                            volume,value,
                            tankVolume,tankValue,
                            caskVolume,caskValue,
                            bulkContainerVolume,bulkContainerValue"
                   totalFields="totalVolume,totalValue,totalTankVolume,totalTankValue,totalCaskVolume,totalCaskValue,totalBulkContainerVolume,totalBulkContainerValue"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="spirit => $router.push({name:'SpiritsTransactions',params:{spiritId:spirit.id},query:{siteId:filters.siteId}})"
                   :item-class="spirit => (spirit.active ? '' : 'grey--text')"
                   title="Spirits"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,
                                          filters.lowInventory=='1'?'Low inventory only':'',
                                          filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                          filters.siteId!=null?siteName:'']}"
                   download-endpoint="spiritsExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.siteId=null;filters.status='1';filters.lowInventory='0';filters.license='0';filters.asof=today;filters.search=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'SKU',value:'sku'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.lowInventory" true-value="1" false-value="0" label="Low inventory only" class="mt-n4"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="License" dense outlined :items="[{value:'0',text:'All'},{divider:true},{value:'1',text:'Excise spirits'},{value:'2',text:'Excise user'}]" v-model="filters.license"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.lowInventory=='1'" close small class="ml-1" @click:close="filters.lowInventory='0'">Low inventory only</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
          <v-chip v-show="filters.license!='0'" close small class="ml-1" @click:close="filters.license='0'">{{filters.license=='2'?'Excise user':'Excise spirits'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'SpiritsEditNew'}"><v-list-item-title>Create spirit</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Spirits',to:{name:'Spirits'}}]},query:{to:'reports/production/ReportsSpiritsActivity'}}"><v-list-item-title>Spirits activity report</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Spirits',to:{name:'Spirits'}}]},query:{to:'reports/inventory/ReportsSpiritsPeriodSummary'}}"><v-list-item-title>Spirits summary report</v-list-item-title></v-list-item>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-divider/>
          <v-list-item @click="doImport=true"><v-list-item-title>Import spirits from CSV</v-list-item-title></v-list-item>
        </template>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync spirits with QBO</v-list-item-title></v-list-item>
        </template>
      </template>
      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <spirits-batch-edit :spirits="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing || readOnly">{{item.name}}</template>
        <v-hover v-else v-slot="{hover}">
          <a @click.stop="$router.push({name:'SpiritsEdit',params:{spiritId:item.id}})"
              :class="{'text-decoration-underline':hover}">{{item.name}}</a>
        </v-hover>
        <div v-if="!item.active && !printing" class="grey--text text-caption">Inactive</div>
      </template>
      <template v-slot:item.tankVolume="{item}">
        {{item.tankVolume | number(3)}}
      </template>
      <template v-slot:item.bulkContainerVolume="{item}">
        {{item.bulkContainerVolume | number(3)}}
      </template>
      <template v-slot:item.caskVolume="{item}">
        {{item.caskVolume | number(3)}}
      </template>
      <template v-slot:item.volume="{item,printing}">
        <template v-if="printing">
          {{item.volume | number(3)}}
        </template>
        <span v-else :class="{'warning--text':item.lowInventory}">{{item.volume | number(3)}}</span>
        <div v-if="!printing && item.lowInventory" class="text-caption">
          <v-btn rounded color="warning lighten-1" x-small :to="{name:'PurchasingPurchaseOrdersEditNew',params:{itemType:'Spirit',itemId:item.id}}">Create PO</v-btn>
        </div>
      </template>
      <template v-slot:item.tankValue="{value}">
        {{value | number(2)}}
      </template>
      <template v-slot:item.bulkContainerValue="{value}">
        {{value | number(2)}}
      </template>
      <template v-slot:item.caskValue="{value}">
        {{value | number(2)}}
      </template>
      <template v-slot:item.value="{value}">
        {{value | number(2)}}
      </template>
      <template v-slot:item.onOrder="{item}">
        <template v-if="item.onOrder">
          {{item.onOrder | number(3)}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:item.onSalesOrder="{item}">
        <template v-if="item.onSalesOrder">
          {{item.onSalesOrder | number(3)}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'SpiritsEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Spirit operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'SpiritsTransactions',params:{spiritId:item.id},query:{siteId:filters.siteId}}"><v-list-item-title>Transactions</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'PurchasingPurchaseOrdersEditNew',params:{vendorId:item.preferredVendor?item.preferredVendor.id:'',itemType:'Spirit',itemId:item.id}}"><v-list-item-title>Create purchase order</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'PurchasingInventoryReceiptsEditNew',params:{vendorId:item.preferredVendor?item.preferredVendor.id:'',itemType:'Spirit',itemId:item.id}}"><v-list-item-title>Create inventory receipt</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'SpiritsEdit',params:{spiritId:item.id}}"><v-list-item-title>Edit spirit</v-list-item-title></v-list-item>
              <v-list-item @click="spirit=item;showDelete=true"><v-list-item-title>Delete spirit</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='tankVolume'">
              {{totals.totalTankVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='bulkContainerVolume'">
              {{totals.totalBulkContainerVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='caskVolume'">
              {{totals.totalCaskVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='volume'">
              {{totals.totalVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='tankValue'">
              {{totals.totalTankValue | number(2)}}
            </td>
            <td class="text-right" v-else-if="header.value==='bulkContainerValue'">
              {{totals.totalBulkContainerValue | number(2)}}
            </td>
            <td class="text-right" v-else-if="header.value==='caskValue'">
              {{totals.totalCaskValue | number(2)}}
            </td>
            <td class="text-right" v-else-if="header.value==='value'">
              {{totals.totalValue | number(2)}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="spirit.name" @delete="performDeleteSpirit"/>
  </div>
</template>

<script>
import SpiritsBatchEdit from '@/views/alcohol/spirits/SpiritsBatchEdit'
import {format} from 'date-fns'

export default {
  name: 'Spirits',
  components: {SpiritsBatchEdit},
  data() {
    return {
      filters: {
        lowInventory: '0',
        asof: format(new Date(), 'yyyy-MM-dd'),
        status: '1',
        license: '0',
        siteId: null,
        search: '',
        searchField: 'name'
      },
      lowInventoryCount: 0,
      showStatus: false,
      today: format(new Date(), 'yyyy-MM-dd'),
      searchDisplay: '',
      spirit: {},
      totals: {totalVolume:0, totalValue:0,
               totalTankVolume:0, totalTankValue: 0,
               totalCaskVolume:0, totalCaskValue: 0,
               totalBulkContainerVolume:0, totalBulkContainerValue: 0},
      showDelete: false,
      doImport: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true},{name:'SKU',mapTo:'',mandatory:false}],
      instructions: `<ol class="mb-4">
                        <li>Name</li>
                        <li>SKU (optional)</li>
                      </ol>
                      <em>Note that not all information for a spirit can be imported.</em>
                      You should, therefore, check each imported spirit to ensure all settings are correct.`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'sku', visible:false},
                       { value: 'onOrder', visible:false},
                       { value: 'onSalesOrder', visible:false},
                       { value: 'tankVolume', visible:true},
                       { value: 'bulkContainerVolume', visible:true},
                       { value: 'caskVolume', visible:true},
                       { value: 'volume', visible:true, excludeFromExport:true},
                       { value: 'tankValue', visible:false},
                       { value: 'bulkContainerValue', visible:false},
                       { value: 'caskValue', visible:false},
                       { value: 'value', visible:false, excludeFromExport:true}],
      headers: [{ text: 'SPIRIT', value: 'name', width:'30%',sortable:true},
                { text: 'SKU', value: 'sku', width:'10%',sortable:true},
                { text: 'ON PO', value: 'onOrder', width:'15%',sortable:false,align:'right'},
                { text: 'ON SO', value: 'onSalesOrder', width:'15%',sortable:false,align:'right'},
                { text: 'TANKS LAA', value: 'tankVolume', width:'13%',align:'right',sortable:true},
                { text: 'CONTAINERS LAA', value: 'bulkContainerVolume', width:'13%',sortable:true,align:'right'},
                { text: 'CASKS LAA', value: 'caskVolume', width:'13%',sortable:true,align:'right'},
                { text: 'TOTAL LAA', value: 'volume', width:'16%',sortable:true,align:'right'},
                { text: 'TANKS $', value: 'tankValue', width:'13%',align:'right',sortable:true},
                { text: 'CONTAINERS $', value: 'bulkContainerValue', width:'13%',sortable:true,align:'right'},
                { text: 'CASKS $', value: 'caskValue', width:'13%',sortable:true,align:'right'},
                { text: 'TOTAL $', value: 'value', width:'16%',sortable:true,align:'right'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true, {B266:true,B263:true});
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write') &&
             !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.lowInventory == '1')
        filters.push({field:'lowInventory', comparator:'eq', value:'1'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});

      return filters;
    },
    listParameters() {
      const parameters = [];
      if (this.filters.asof)
        parameters.push({name:'asof', value:`${this.filters.asof} 23:59:59`});
      if (this.filters.siteId)
        parameters.push({name:'siteId', value:this.filters.siteId});
      if (this.filters.license != '0')
        parameters.push({name:'userLicense', value:this.filters.license == '1' ? 'false' : 'true'});
      return parameters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');

    const results = await this.$apiCall(`query Spirits {
                                            spiritsLowInventory
                                          }`,
                                      {});
    if (!results.error)
      this.lowInventoryCount = results.data.spiritsLowInventory;

    if (!['SpiritsEdit', 'SpiritsTransactions'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.lowInventoryCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    async performDeleteSpirit() {
      this.loading = true;
      const results = await this.$apiCall(`mutation Spirits($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.spirit.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('setError', {type:'success',message:`Deleted spirit ${this.spirit.name}`});
        this.$refs.table?.buildList();
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation Spirits {
                                             accountingIntegrationSync(step:"spirits") {name}
                                           }`, {});
      this.$refs.table?.buildList();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced spirits with QBO`});
    }
  }
}
</script>